// src/components/tour/tours/alerts.jsx
//
// Alert Dashboard tour - one component (AlertDashboard.jsx) serves both
// /admin/alerts and the client /alerts page; FleetHeader's scope picks
// the wording (clients never see Starlink overage or Data Center).

import React from 'react';
import { replayStep } from './common';

export function getAlertsSteps(ctx, env) {
    const {
        totalAlerts = 0,
        windowLabel = 'ALL TIME',
        topTypeLabel,
        topTypePct,
        topDeviceName,
        topDeviceCount,
        uniqueDeviceCount = 0,
        firstAlert,
        shouldHideStarlink,
    } = ctx;
    const { isMobile, scope } = env;
    const isClient = scope === 'client';
    const click = isMobile ? 'Tap' : 'Click';

    const typeList = (
        <>
            <b>WAN status</b> (an internet link went up/down), <b>Client status</b> (an important device
            dropped off or came back), <b>Usage cap</b> (a VLAN or WAN reached its data limit)
            {!shouldHideStarlink && <>, <b>Starlink overage</b> (a line went over its plan)</>}
            {' '}and <b>SF VPN</b> (SpeedFusion tunnel status).
        </>
    );

    return [
        {
            target: null,
            title: 'Welcome to the Alert Dashboard',
            content: (
                <>
                    <p>
                        Every alert raised for {isClient ? 'your routers' : 'the fleet'} in one place:
                        {' '}<span className="pt-data">{totalAlerts}</span> alert{totalAlerts === 1 ? '' : 's'}
                        {' '}from <span className="pt-data">{uniqueDeviceCount}</span> device{uniqueDeviceCount === 1 ? '' : 's'}
                        {' '}({windowLabel.toLowerCase()}).
                    </p>
                    <p>The list refreshes by itself every 3 minutes.</p>
                </>
            ),
        },

        {
            target: '[data-tour="al-summary"]',
            title: 'Alerts by type',
            content: (
                <>
                    <p>{typeList}</p>
                    {!isMobile && <p>Each box is also a filter: {click.toLowerCase()} one to list only that type.</p>}
                    {topTypeLabel && totalAlerts > 0 && (
                        <p>Right now <span className="pt-data">{topTypeLabel}</span> makes up <span className="pt-data">{topTypePct}%</span> of alerts.</p>
                    )}
                </>
            ),
        },

        {
            layout: 'mobile',
            target: '[data-tour="al-categories"]',
            title: 'Filter by type',
            content: <p>Tap a category to list only those alerts. Tap <b>Total</b> above to see everything again.</p>,
        },

        {
            layout: 'desktop',
            target: '[data-tour="al-charts"]',
            title: 'Where alerts come from',
            content: (
                <>
                    <p>
                        The split by alert type and by device class, and the 10 devices raising the most alerts
                        {topDeviceName && <> (top: <span className="pt-data">{topDeviceName}</span> with <span className="pt-data">{topDeviceCount}</span>)</>}.
                    </p>
                    <p>A device that keeps flapping is usually worth a closer look first.</p>
                </>
            ),
        },

        {
            target: '[data-tour="al-filters"]',
            title: 'Narrow it down',
            content: isMobile ? (
                <p>
                    <b>Last Hour</b> for what's happening right now, then filter by device type
                    (Fully Managed, WAN Managed{!isClient && ', Data Center'}) or date range.
                </p>
            ) : (
                <p>
                    Search by device, item, IP or VLAN, then filter by type, device class
                    (Fully Managed, WAN Managed{!isClient && ', Data Center'}) and date.
                    {' '}<b>LAST 1 HOUR</b> shows what's happening right now, <b>CLEAR</b> resets everything
                    and <b>REFRESH</b> reloads without waiting.
                </p>
            ),
        },

        {
            layout: 'mobile',
            target: '[data-tour="al-search"]',
            title: 'Search',
            content: <p>Search by device name, item, IP address or VLAN.</p>,
        },

        {
            target: '[data-tour="al-first"]',
            title: 'Reading an alert',
            content: firstAlert ? (
                <>
                    <p>
                        The newest alert: <span className="pt-data">{firstAlert.device}</span>,
                        type <span className={`alert-type-chip ${firstAlert.type.cls}`}>{firstAlert.type.label}</span>.
                    </p>
                    <p>
                        {isMobile
                            ? 'Each card shows the time, device, affected item and its status change (before → after). Tap it for full details.'
                            : 'Each row shows when it happened, which device and item, its IP, and the status change (before → after).'}
                    </p>
                </>
            ) : null,
        },

        replayStep,
    ];
}
